import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Brain, MessageSquare, Users, ChevronRight, Bot, User, ArrowLeft, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';

// ── Message bubble (read-only) ────────────────────────────────────────────────
const MsgBubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={cn('flex gap-2.5 mb-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
      <div className={cn(
        'w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-xs',
        isUser ? 'bg-primary/10 text-primary' : 'bg-violet-100 text-violet-600'
      )}>
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      <div className={cn(
        'max-w-[70%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
        isUser ? 'bg-primary/10 text-foreground' : 'bg-card border border-border text-foreground shadow-sm'
      )}>
        {msg.content}
        {msg.created_at && (
          <div className={cn('text-[10px] mt-1 opacity-50', isUser ? 'text-right' : 'text-left')}>
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
};

const PAGE_LIMIT = 20;

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AIChatsPage() {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ total_sessions: 0, total_messages: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionMeta, setSessionMeta] = useState(null);   // { customer_name, customer_email, model_name, total_messages }
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [msgPage, setMsgPage] = useState(1);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const scrollRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isFirstLoad = useRef(true);

  const LIMIT = 20;

  // ── Sessions list ──────────────────────────────────────────────────────────
  const fetchSessions = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const [sessRes, statsRes] = await Promise.all([
        companyAdminCustomersAPI.getAiChatSessions({ page: p, limit: LIMIT }),
        companyAdminCustomersAPI.getAiChatStats(),
      ]);
      setSessions(sessRes.data.sessions || []);
      setTotal(sessRes.data.total || 0);
      setStats(statsRes.data);
      setPage(p);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ── Open a session (first page = latest 20) ────────────────────────────────
  const openSession = async (sid) => {
    setSelectedSession(sid);
    setMessages([]);
    setSessionMeta(null);
    setHasMore(false);
    setMsgPage(1);
    isFirstLoad.current = true;
    setDetailLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getAiChatSession(sid, { page: 1, limit: PAGE_LIMIT });
      const d = res.data;
      setSessionMeta({
        customer_name: d.customer_name,
        customer_email: d.customer_email,
        model_name: d.model_name,
        provider: d.provider,
        total_messages: d.total_messages,
      });
      setMessages(d.messages || []);
      setHasMore(d.has_more || false);
      setMsgPage(1);
    } catch {}
    finally { setDetailLoading(false); }
  };

  // ── Load older messages on scroll to top ──────────────────────────────────
  const loadMoreMessages = useCallback(async () => {
    if (!selectedSession || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = msgPage + 1;
    try {
      const res = await companyAdminCustomersAPI.getAiChatSession(selectedSession, { page: nextPage, limit: PAGE_LIMIT });
      const d = res.data;
      // Save scroll height before prepending so we can restore position
      if (scrollRef.current) {
        prevScrollHeightRef.current = scrollRef.current.scrollHeight;
      }
      setMessages(prev => [...(d.messages || []), ...prev]);
      setHasMore(d.has_more || false);
      setMsgPage(nextPage);
    } catch {}
    finally { setLoadingMore(false); }
  }, [selectedSession, loadingMore, hasMore, msgPage]);

  // ── Auto-scroll to bottom on first load ───────────────────────────────────
  useEffect(() => {
    if (!scrollRef.current || detailLoading) return;
    if (isFirstLoad.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      isFirstLoad.current = false;
    }
  }, [messages, detailLoading]);

  // ── Restore scroll position after prepending older messages ───────────────
  useEffect(() => {
    if (!loadingMore && scrollRef.current && prevScrollHeightRef.current > 0) {
      const newScrollHeight = scrollRef.current.scrollHeight;
      scrollRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [loadingMore, messages]);

  // ── Infinite scroll handler ───────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    if (scrollRef.current.scrollTop < 80 && hasMore && !loadingMore) {
      loadMoreMessages();
    }
  }, [hasMore, loadingMore, loadMoreMessages]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <CompanyAdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI Chats</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor AI chat conversations with customers</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 max-w-sm">
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-violet-500" />
              <span className="text-xs text-muted-foreground">Total Sessions</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats.total_sessions}</div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">Total Messages</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats.total_messages}</div>
          </div>
        </div>

        <div className="flex gap-4" style={{ height: 'calc(100vh - 260px)' }}>
          {/* Sessions list */}
          <div className="w-80 shrink-0 bg-card rounded-xl border border-border flex flex-col">
            <div className="px-4 py-3 border-b border-border">
              <span className="text-sm font-semibold text-foreground">Conversations ({total})</span>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <Brain className="w-8 h-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No AI conversations yet</p>
              </div>
            ) : (
              <>
                <div className="app-modal-body flex-1 overflow-y-auto min-h-0">
                  {sessions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => openSession(s.id)}
                      data-testid={`session-item-${s.id}`}
                      className={cn(
                        'w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors flex items-start justify-between gap-2',
                        selectedSession === s.id && 'bg-primary/5 border-l-2 border-l-primary'
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Brain className="w-3 h-3 text-violet-500 shrink-0" />
                          <span className="text-xs font-medium text-foreground truncate">{s.model_name || 'AI Chat'}</span>
                          <span className="text-[10px] text-muted-foreground">{s.provider}</span>
                        </div>
                        {/* Customer name / email */}
                        {(s.customer_name || s.customer_email) && (
                          <div className="flex items-center gap-1 mb-0.5">
                            <User className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="text-xs text-foreground font-medium truncate">
                              {s.customer_name || s.customer_email}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground truncate">
                          {s.last_message_role === 'assistant' ? '🤖 ' : s.last_message_role === 'user' ? '👤 ' : ''}
                          {s.last_message || 'No messages'}
                        </p>
                        <p className="text-[10px] text-muted-foreground/70 mt-1">
                          {new Date(s.updated_at).toLocaleString()}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                    </button>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-2 border-t border-border">
                    <button
                      disabled={page <= 1}
                      onClick={() => fetchSessions(page - 1)}
                      className="text-xs text-primary disabled:opacity-40"
                    >Prev</button>
                    <span className="text-xs text-muted-foreground">{page}/{totalPages}</span>
                    <button
                      disabled={page >= totalPages}
                      onClick={() => fetchSessions(page + 1)}
                      className="text-xs text-primary disabled:opacity-40"
                    >Next</button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Session detail */}
          <div className="flex-1 bg-card rounded-xl border border-border flex flex-col min-w-0">
            {!selectedSession ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Brain className="w-10 h-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">Select a conversation to view messages</p>
              </div>
            ) : detailLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Header with customer info */}
                <div className="px-4 py-3 border-b border-border flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => { setSelectedSession(null); setSessionMeta(null); setMessages([]); }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Brain className="w-4 h-4 text-violet-500 shrink-0" />
                      <span className="text-sm font-semibold text-foreground">{sessionMeta?.model_name}</span>
                      {sessionMeta?.provider && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{sessionMeta.provider}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs font-medium text-foreground truncate">
                        {sessionMeta?.customer_name || 'Unknown Customer'}
                      </span>
                      {sessionMeta?.customer_email && (
                        <span className="text-xs text-muted-foreground truncate">· {sessionMeta.customer_email}</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">
                        {sessionMeta?.total_messages || 0} messages
                      </span>
                    </div>
                  </div>
                </div>

                {/* Messages area */}
                <div
                  ref={scrollRef}
                  className="app-modal-body flex-1 overflow-y-auto min-h-0 p-4"
                  onScroll={handleScroll}
                >
                  {/* Load more indicator at top */}
                  {loadingMore && (
                    <div className="flex justify-center py-2 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {hasMore && !loadingMore && (
                    <div className="flex justify-center py-2 mb-2">
                      <button
                        onClick={loadMoreMessages}
                        className="text-xs text-primary hover:underline"
                      >
                        Load older messages
                      </button>
                    </div>
                  )}

                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                      <MessageSquare className="w-8 h-8 text-muted-foreground/30 mb-2" />
                      <p className="text-sm text-muted-foreground">No messages in this session</p>
                    </div>
                  ) : (
                    messages.map((msg, i) => <MsgBubble key={i} msg={msg} />)
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </CompanyAdminLayout>
  );
}
